/** How Fleet works, in the words its screens use. Everyone who can sign in can read it. */
export function Help() {
  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">How Fleet works</div>
          <div className="page-subtitle">The register of every vehicle, tractor and trailer</div>
        </div>
      </div>

      <div className="help__body">
        <section className="help__section">
          <h2 className="help__h2">What it is for</h2>
          <p>
            Fleet replaces the Master Vehicle List spreadsheet. It answers what we run, what each unit is, who owns
            it, where it works and who drives it — and it keeps a record of every change, so "who changed this plate"
            always has an answer.
          </p>
          <p>
            <strong>One unit is one physical vehicle, tractor or trailer.</strong> A route such as GKC617 or Boon905
            is not a unit: it is the run a truck does, and Inventory counts the stock on it. A unit can be linked to
            the route it runs, so a route with three trucks shows three units.
          </p>
        </section>

        <section className="help__section">
          <h2 className="help__h2">The register</h2>
          <ul className="help__list">
            <li>
              <strong>Find</strong> matches any part of a <strong>name</strong>, VIN, plate, driver, route, region, home location or vendor —
              typing <em>1255</em> finds the Rush truck whose name contains it.
            </li>
            <li>
              The lists narrow by <strong>kind</strong>, <strong>status</strong>, <strong>origin</strong>,{' '}
              <strong>region</strong> and <strong>ownership</strong>, several at once. Every column heading sorts.
            </li>
            <li>
              The boxes at the top count what is in the fleet now — units Returned or Sold or disposed aren't
              counted. The first row is by kind, plus <strong>Needs review</strong>; the second is{' '}
              <strong>Active</strong> and <strong>In the shop</strong> (by status) and <strong>Rented</strong> and{' '}
              <strong>Leased</strong> (by ownership). Click one to show just those.
            </li>
            <li>
              Click a row to open the unit's record, or <strong>Edit</strong> at the end of the row to go straight to
              its form.
            </li>
            <li>
              The <strong>Region</strong> list has <em>— No region yet —</em> at the bottom: the units still to be
              placed.
            </li>
            <li><strong>Export</strong> saves exactly what is on screen, filtered and sorted.</li>
            <li>
              On a <strong>phone</strong> the register is one card per unit — name, status, vehicle, plate, ownership,
              driver and region — with Edit on each, and the unit form lays its labels above the boxes.
            </li>
          </ul>
        </section>

        <section className="help__section">
          <h2 className="help__h2">Needs review</h2>
          <p>
            A unit is flagged when something in its record needs a person to look — a VIN that fails its check, a
            plate that is on two units, an ownership the list recorded two ways, a unit named after its driver. The
            flag always says <em>why</em>, in words, on the unit's record.
          </p>
          <p>
            Once the record is right, <strong>Mark as reviewed</strong> clears the flag. Loading the list again never
            re-flags a unit someone has reviewed.
          </p>
        </section>

        <section className="help__section">
          <h2 className="help__h2">A unit's record</h2>
          <ul className="help__list">
            <li><strong>Edit</strong> changes it. A unit needs a <strong>name</strong> (Advo NJ01, CYN1033) or a VIN; everything else can be filled in as it is known.</li>
            <li>
              The <strong>driver</strong> is picked from the HR employee list, one per unit. Changing it asks from which
              day: the old driver's time ends that day and the new driver's starts, and the unit's{' '}
              <strong>Drivers</strong> list keeps everyone who has driven it, with dates.
            </li>
            <li>
              <strong>Origin</strong> (Cyntox, Advo or Helios) is only a label for reporting — one company, and it
              changes nobody's access.
            </li>
            <li>
              <strong>Ownership</strong> is one of six: <em>Owned outright</em>, <em>Financed</em>,{' '}
              <em>Operating lease</em>, <em>Financed lease</em>, <em>Long-term rental</em> or{' '}
              <em>Short-term rental</em>. An owned unit's owner is always <strong>Cyntox</strong>, shown as plain text
              rather than a box, and nothing about owning it ends. Every other kind has a vendor or lender and an end
              date — <em>Financing ends</em>, <em>Lease ends</em> or <em>Rental ends</em>, named for what it is.
            </li>
            <li>
              <strong>Region</strong> (East, Heartland or Midwest) and <strong>Home location</strong> (the facility or
              storage unit it is based at) come from Admin, the same list Inventory uses. Picking a home sets the region;
              the home list only offers places in the region chosen. The <strong>route</strong> is the Inventory route it
              runs, if it runs one.
            </li>
            <li>
              A <strong>VIN</strong> is one unit for ever, so two units can't share one. A name can be reused once the
              unit it belonged to is Returned or Sold or disposed.
            </li>
            <li>
              A unit loaded from the spreadsheet keeps its row exactly as it was — <strong>Show row … as it
              was</strong> — so nothing tidied up on the way in is lost.
            </li>
            <li>History lists every change; the full before and after of each is in Admin's Audit Log, under Fleet.</li>
          </ul>
        </section>

        <section className="help__section">
          <h2 className="help__h2">Where the register came from</h2>
          <p>
            The register was loaded from Beth's Master Vehicle List. Fleet is where units are kept now: add and change
            them here. There is no Import screen. If a list ever does need loading, Marsha has it checked first — what
            it would add, change or skip — before anything is saved.
          </p>
          <div className="help__callout">
            <div className="help__callout-label">How the list was read</div>
            <ul className="help__list">
              <li>The empty "Dupes to return" rows — no name, VIN or plate — are skipped and listed.</li>
              <li>Returned rentals come in as Returned, so their history is kept.</li>
              <li>
                Where the list records ownership twice and the two disagree, the <strong>Ownership</strong> and{' '}
                <strong>Vendor</strong> columns win — a lender named means financed — and the unit is flagged.
              </li>
              <li>
                A bare "Lease" or "Rental" doesn't say which kind, so it comes in as an operating lease or a short-term
                rental and is flagged for someone to confirm. "Weekly" and "No Contract" rentals are short term.
              </li>
              <li>An owned unit's vendor is always Cyntox, and it has no end date.</li>
              <li>
                The <strong>Group</strong> column gives the unit's region: East, Heartland or Midwest, or a place in one —
                INDY and PA set Indianapolis TS and Philadelphia TS as home; GKC, Omaha, Tulsa, Lake Regional and Texas are
                Heartland. Anything else is flagged for someone to pick the region. A blank Group takes the route's region.
              </li>
              <li>A unit whose Use or description names a route (GKC-611, OH Swing) is linked to that route. A near miss is left for a person.</li>
              <li>Loading the same list twice changes nothing; a newer copy updates the units it already added.</li>
              <li>
                A blank cell in a newer copy never erases what the register already has. A unit's driver and route are
                set in the app: the list fills them in only where a unit has none.
              </li>
            </ul>
          </div>
        </section>

        <section className="help__section">
          <h2 className="help__h2">Shops</h2>
          <p>
            Where units go for repairs, and each time one went — Beth's Shop Locations tab, kept in two parts.
          </p>
          <ul className="help__list">
            <li>
              <strong>Visits</strong> opens on the units still at a shop. A visit moves along on its own as its dates
              are filled in: <em>Waiting for estimate</em>, then <em>Estimate to approve</em> once there is an
              estimate, then <em>Approved — in repair</em> or <em>Denied</em>, and <em>Picked up</em> when it's back.
              The tiles at the top count each, and the estimates waiting on someone's approval.
            </li>
            <li>
              <strong>Log a visit</strong> there, or <strong>Send to a shop</strong> on a unit's own page, where its
              visits are listed too. Click a visit to record the estimate, the decision and who made it, or the
              pick-up date.
            </li>
            <li>
              <strong>An estimate can't be approved without the shop's estimate PDF.</strong> On the visit, drag the
              PDF onto the <em>Estimate PDF</em> box, or click the box to choose the file (PDFs only, up to 10 MB). It is
              sent when you save; <em>Don't attach</em> drops it before then. A new visit and its PDF are saved
              together, so a PDF that fails to send doesn't leave a visit behind. Visits logged before October 7, 2026
              are grandfathered and can be approved without one.
            </li>
            <li>
              A revised estimate is attached alongside the first. Click a file's name to download it, or{' '}
              <em>PDF</em> in the Visits list for the newest. <em>Remove</em> takes a PDF off the visit, but the file
              is kept, so what was approved can always be shown — and the last PDF on an approved estimate can't be
              removed until its replacement is attached.
            </li>
            <li>
              <strong>Shop list</strong> is every shop with its address, phone and email. Each branch is its own shop —
              Rush in Columbus and Rush in Indianapolis are two. A shop no longer used is ticked off rather than deleted,
              so its past visits keep it.
            </li>
            <li>A visit doesn't change the unit's status. "In the shop" is still set on the unit itself.</li>
          </ul>
        </section>

        <section className="help__section">
          <h2 className="help__h2">To-do</h2>
          <p>
            A list of action items for the fleet — things to chase, call about or follow up on.
          </p>
          <ul className="help__list">
            <li>
              <strong>Add a to-do</strong> with what needs doing, the date entered (today unless you change it), and the
              date it's expected done. Give it a <em>Type</em> — Vehicle, Trailer, Tractor, Shop / vendor (a shop, or a
              contract such as a national service agreement) or General. For a particular unit, put its name in the
              description; the Find box searches descriptions.
            </li>
            <li>
              The list opens on what is still open. An item past its expected date shows as <em>Overdue</em> in red; the
              tiles at the top count the open, overdue and due-this-week items, and clicking one filters the list. The
              <em> Type</em> filter shows just one kind, such as trailers.
            </li>
            <li>
              <strong>Tick the box</strong> when it's done: it records today and who ticked it. Click the item to change
              the date done or write the <em>Outcome</em> — what was done or what came of it. Untick to reopen it.
            </li>
            <li>
              <strong>Remove</strong> is for an item added by mistake; a finished one is ticked instead, so the list keeps
              what was done. Every change, including a removal, is in the Audit Log.
            </li>
          </ul>
        </section>

        <section className="help__section">
          <h2 className="help__h2">Who can do what</h2>
          <ul className="help__list">
            <li><strong>Viewer</strong> sees the register and each unit's record, for its regions.</li>
            <li>
              <strong>Fleet Admin</strong> also adds units, changes every part of them including drivers and ownership,
              logs shop visits, and keeps the to-do list.
            </li>
            <li>
              Access is given in Admin → Access, like every other app's, by region: a role sees and changes the units in
              its regions. A unit with no region yet is seen only by a role that reaches every region, so it gets placed.
            </li>
            <li>
              Changing a unit is three permissions, so each part can be given to the person who owns it:{' '}
              <strong>Add and change vehicles</strong> (what it is, registration, status, region, route),{' '}
              <strong>Change drivers</strong>, and <strong>Change ownership</strong> (origin, owned, financed, leased or
              rented, vendor and dates). On the form, a part someone's access doesn't cover is shown but locked.
            </li>
            <li>
              <strong>Log shop visits</strong> is its own permission: logging and moving along a unit's shop visits in
              its regions, attaching and removing estimate PDFs, and keeping the shop list. Anyone who can see the
              register can see the shops and visits, and download their estimate PDFs.
            </li>
            <li>
              <strong>Keep the to-do list</strong> is its own permission too: adding, changing, ticking off and removing
              items. The list isn't split by region: anyone who can see the register sees every item.
            </li>
          </ul>
        </section>

        <section className="help__section">
          <h2 className="help__h2">Coming later</h2>
          <p>
            Registration and inspection reminders, the Motive mileage sync, and what each unit costs. Reminders need
            the registration dates, which about a third of units have so far (42 of 141 in October 2026) — filling
            those in on each record is what makes them possible.
          </p>
        </section>
      </div>
    </div>
  );
}
